import { useState, useEffect } from "react";
import {
  Wallet,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Copy,
  RefreshCw,
} from "lucide-react";

const EthDepositPage = () => {
  const [ethAmount, setEthAmount] = useState("");
  const [points, setPoints] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [depositStatus, setDepositStatus] = useState("idle"); // idle, pending, success, error
  const [ethPrice] = useState(2650); // Giá ETH giả định
  const [exchangeRate] = useState(1000); // 1 ETH = 1000 điểm
  const [walletAddress] = useState(
    "0x742d35Cc6634C0532925a3b8D654bEcE2D6B5D4A"
  );
  const [balance] = useState(12.5); // ETH balance giả định
  const [copied, setCopied] = useState(false);

  // Tính điểm dựa trên số ETH
  useEffect(() => {
    if (ethAmount && !isNaN(parseFloat(ethAmount))) {
      setPoints(parseFloat(ethAmount) * exchangeRate);
    } else {
      setPoints(0);
    }
  }, [ethAmount, exchangeRate]);

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeposit = async () => {
    if (!ethAmount || parseFloat(ethAmount) <= 0) {
      alert("Vui lòng nhập số ETH hợp lệ");
      return;
    }

    if (parseFloat(ethAmount) > balance) {
      alert("Số dư không đủ");
      return;
    }

    setIsLoading(true);
    setDepositStatus("pending");

    // Giả lập quá trình giao dịch
    try {
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Giả lập hash giao dịch
      const mockTxHash = "0x" + Math.random().toString(16).substring(2, 66);
      setTxHash(mockTxHash);
      setDepositStatus("success");
    } catch (error) {
      setDepositStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEthAmount("");
    setPoints(0);
    setDepositStatus("idle");
    setTxHash("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-4">
            <Wallet className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Nạp Tiền</h1>
          <p className="text-white/70">Chuyển đổi ETH thành điểm</p>
        </div>

        {depositStatus === "idle" && (
          <>
            {/* Thông tin tỷ giá */}
            <div className="bg-white/5 rounded-2xl p-4 mb-6 border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/70 text-sm">Giá ETH hiện tại</span>
                <span className="text-white font-semibold">
                  ${ethPrice.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/70 text-sm">Tỷ lệ chuyển đổi</span>
                <span className="text-white font-semibold">
                  1 ETH = {exchangeRate.toLocaleString()} điểm
                </span>
              </div>
            </div>

            {/* Địa chỉ ví */}
            <div className="mb-6">
              <label className="block text-white/70 text-sm mb-2">
                Địa chỉ ví của bạn
              </label>
              <div className="flex items-center bg-white/5 rounded-xl p-3 border border-white/10">
                <span className="text-white text-sm flex-1 truncate">
                  {walletAddress}
                </span>
                <button
                  onClick={handleCopyAddress}
                  className="ml-2 p-1 text-white/70 hover:text-white transition-colors"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="text-right mt-1">
                <span className="text-white/50 text-xs">
                  Số dư: {balance} ETH
                </span>
              </div>
            </div>

            {/* Input số ETH */}
            <div className="mb-6">
              <label className="block text-white/70 text-sm mb-2">
                Số lượng ETH
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={ethAmount}
                  onChange={(e) => setEthAmount(e.target.value)}
                  placeholder="0.0"
                  step="0.001"
                  min="0"
                  max={balance}
                  className="w-full bg-white/5 border border-white/20 rounded-xl p-4 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/50 text-sm">
                  ETH
                </span>
              </div>
            </div>

            {/* Hiển thị điểm nhận được */}
            {points > 0 && (
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-xl p-4 mb-6 border border-purple-500/30">
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Điểm nhận được</span>
                  <ArrowRight className="w-5 h-5 text-white/50" />
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-white">
                    {points.toLocaleString()}
                  </span>
                  <span className="text-white/70 ml-1">điểm</span>
                </div>
              </div>
            )}

            {/* Nút nạp tiền */}
            <button
              onClick={handleDeposit}
              disabled={!ethAmount || parseFloat(ethAmount) <= 0 || isLoading}
              className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold py-4 px-6 rounded-xl hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Đang xử lý...
                </div>
              ) : (
                "Nạp Tiền"
              )}
            </button>
          </>
        )}

        {depositStatus === "pending" && (
          <div className="text-center py-8">
            <RefreshCw className="w-16 h-16 text-purple-400 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Đang xử lý giao dịch
            </h3>
            <p className="text-white/70 mb-4">Vui lòng đợi trong giây lát...</p>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between mb-2">
                <span className="text-white/70">Số ETH</span>
                <span className="text-white font-semibold">
                  {ethAmount} ETH
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Điểm nhận được</span>
                <span className="text-white font-semibold">
                  {points.toLocaleString()} điểm
                </span>
              </div>
            </div>
          </div>
        )}

        {depositStatus === "success" && (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Nạp tiền thành công!
            </h3>
            <p className="text-white/70 mb-4">Giao dịch đã được xác nhận</p>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-white/70">Số ETH đã nạp</span>
                <span className="text-white font-semibold">
                  {ethAmount} ETH
                </span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-white/70">Điểm đã nhận</span>
                <span className="text-green-400 font-semibold">
                  +{points.toLocaleString()} điểm
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">Mã giao dịch</span>
                <span className="text-blue-400 text-sm font-mono">
                  {txHash.substring(0, 10)}...
                </span>
              </div>
            </div>
            <button
              onClick={resetForm}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-300"
            >
              Nạp tiền tiếp
            </button>
          </div>
        )}

        {depositStatus === "error" && (
          <div className="text-center py-8">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Giao dịch thất bại
            </h3>
            <p className="text-white/70 mb-6">
              Có lỗi xảy ra, vui lòng thử lại
            </p>
            <button
              onClick={resetForm}
              className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-red-600 hover:to-pink-600 transition-all duration-300"
            >
              Thử lại
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="text-white/50 text-xs">
            Giao dịch được bảo mật bằng công nghệ blockchain
          </p>
        </div>
      </div>
    </div>
  );
};

export default EthDepositPage;
